// src/components/admin/kontroly/DokumentyTab.tsx
import React, { useRef, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import {
    useKontrolyDokumentyQuery,
    useUploadDokumentMutation,
    useUpdateDokumentMutation,
    useDeleteDokumentMutation,
    useDownloadDokumentMutation,
} from '../../../api/JsdhNotifikaceService/service/KontrolyDokumentMutations';
import { downloadFile } from '../../../utils/downloadFile';
import { useToast } from '../../../components/toast/useToast';

const MAX_SIZE_MB = 7;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

interface DokumentyTabProps {
    typId: number | null;
}

const DokumentyTab: React.FC<DokumentyTabProps> = ({ typId }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showToast } = useToast();
    const [deleteTarget, setDeleteTarget] = useState<{ id: number; nazev: string } | null>(null);

    const { data: dokumenty, isLoading, error } = useKontrolyDokumentyQuery(typId);
    const uploadMutation = useUploadDokumentMutation(typId ?? 0);
    const updateMutation = useUpdateDokumentMutation();
    const deleteMutation = useDeleteDokumentMutation();
    const downloadMutation = useDownloadDokumentMutation();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !typId) return;
        if (file.size > MAX_SIZE_BYTES) {
            showToast(`Soubor je příliš velký. Maximum je ${MAX_SIZE_MB} MB.`, 'error');
            return;
        }
        uploadMutation.mutate({ file });
        // reset input so same file can be re-uploaded
        e.target.value = '';
    };

    const handleTogglePripojit = (doc: { id: number; nazev: string; pripojitKEmailu: boolean; poradi: number }) => {
        if (!typId) return;
        updateMutation.mutate({
            id: doc.id,
            typId,
            data: {
                nazev: doc.nazev,
                pripojitKEmailu: !doc.pripojitKEmailu,
                poradi: doc.poradi,
            },
        });
    };

    const handleDownload = (id: number) => {
        downloadMutation.mutate(id, {
            onSuccess: ({ blob, filename }) => downloadFile(blob, filename),
        });
    };

    const handleDeleteConfirm = () => {
        if (deleteTarget === null || !typId) return;
        deleteMutation.mutate(
            { id: deleteTarget.id, typId },
            { onSuccess: () => setDeleteTarget(null) }
        );
    };

    if (!typId) {
        return (
            <Alert severity="info" sx={{ mt: 1 }}>
                Uložte typ kontroly před přidáváním dokumentů.
            </Alert>
        );
    }

    if (isLoading) return <CircularProgress sx={{ mt: 2 }} />;
    if (error) return <Alert severity="error" sx={{ mt: 1 }}>Nepodařilo se načíst dokumenty.</Alert>;

    const totalSize = (dokumenty ?? []).reduce((acc, d) => acc + d.souborVelikost, 0);

    return (
        <Box sx={{ pt: 1 }}>
            {/* Upload */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={
                        uploadMutation.isPending ? <CircularProgress size={16} /> : <CloudUploadIcon />
                    }
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadMutation.isPending}
                >
                    Nahrát dokument
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                />
                <Typography variant="caption" color="text.secondary">
                    Max. {MAX_SIZE_MB} MB — celkem využito: {formatSize(totalSize)} / {MAX_SIZE_MB} MB
                </Typography>
            </Box>

            {/* Seznam dokumentů */}
            {!dokumenty?.length ? (
                <Typography variant="body2" color="text.secondary">
                    Žádné dokumenty. Nahrajte první soubor.
                </Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Název</TableCell>
                                <TableCell>Velikost</TableCell>
                                <TableCell align="center">Přiložit k e-mailu</TableCell>
                                <TableCell align="right">Akce</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(dokumenty ?? []).map((doc) => (
                                <TableRow key={doc.id} hover>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">{doc.nazev}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {doc.souborNazev}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={formatSize(doc.souborVelikost)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            checked={doc.pripojitKEmailu}
                                            onChange={() => handleTogglePripojit(doc)}
                                            disabled={updateMutation.isPending}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Stáhnout">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDownload(doc.id)}
                                                disabled={downloadMutation.isPending}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Smazat">
                                            <IconButton
                                                size="small"
                                                onClick={() => setDeleteTarget({ id: doc.id, nazev: doc.nazev })}
                                                color="error"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Confirm delete dialog */}
            <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Smazat dokument</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Tato akce je nevratná. Dokument „{deleteTarget?.nazev}" bude trvale odstraněn.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Zrušit</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDeleteConfirm}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Smazat'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DokumentyTab;
